import React, { Fragment } from "react";
import type { EcoConsumptionSpecAreaType, EcoConsumptionSpecType } from "../types/EcoConsumptionType";
import styles from "./SpecEcoConsumption.module.scss";
import { useEcoConsumptionOptionalLabel } from "../utils/labels";
import { useCommonSelector } from "../../../../common-deprecated/redux/commonStore";
import { parseSpecificationValue } from "../../../../common-deprecated/utils";

// TDG has determined we should never show ranges here, always pick the lowest value of the range as this is the correct one
const getFirstValueOutOfRange = (value: string): string => {
    const values = value.split(" - ");
    return values[0] || value;
};

export const SpecEcoConsumptionArea: React.FC<
    EcoConsumptionSpecAreaType & {
        getDisclaimerIndex: (disclaimer: SanitizedString) => number;
        PopoverTooltip?: (props: { content: string }) => JSX.Element;
    }
> = (props) => {
    const culture = useCommonSelector(({ commonSettings }) => commonSettings.culture);
    const { title, subtitle, specifications } = props;
    const { getDisclaimerIndex, PopoverTooltip } = props;
    const [ecoConsumptionNotSetLabel, ecoConsumptionNotConfiguredLabel, ecoConsumptionSpecCombineLabel] =
        useEcoConsumptionOptionalLabel([
            "ecoConsumptionNotSet",
            "ecoConsumptionNotConfigured",
            "ecoConsumptionSpecCombine",
        ]);
    const validateSpecification = (spec?: EcoConsumptionSpecType): string | JSX.Element[] => {
        if (!spec || !spec.data.length) return ecoConsumptionNotSetLabel || "";
        return spec.data.map((sp, index) => {
            // eslint-disable-next-line react/no-array-index-key
            if (!sp) return <Fragment key={`undefined-${index}`}>{ecoConsumptionNotConfiguredLabel}</Fragment>;
            return (
                <Fragment key={sp.id}>
                    <span>
                        {parseSpecificationValue(
                            {
                                ...sp,
                                value: getFirstValueOutOfRange(sp.value),
                            },
                            culture,
                        )}
                        {spec.disclaimer && <sup>{getDisclaimerIndex(spec.disclaimer)}</sup>}
                        {PopoverTooltip && sp.tooltip && <PopoverTooltip content={sp.tooltip} />}
                    </span>
                    {index + 1 !== spec.data?.length && ` ${ecoConsumptionSpecCombineLabel || "+"} `}
                </Fragment>
            );
        });
    };

    return (
        <>
            {title && <h4 className={styles.title}>{title}</h4>}
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            {specifications.length && (
                <dl className={styles.list}>
                    {specifications.map((spec, ind) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={`${spec.label}-${ind}`}>
                            <dt data-highlighted={spec.highlighted}>{spec.label}</dt>
                            <dd data-highlighted={spec.highlighted}>{validateSpecification(spec)}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            )}
        </>
    );
};
